// @flow

import React, {Component} from 'react';
import classNames from 'classnames';
import {SORTABLE_FILTER} from '@pb/kit-component-sortable';
import CheckBox from '@pb/kit-component-checkbox';

import {DataTableResize} from './data-table-resize';
import {tableCellName, headerCellName, handleClass, handleClassResize} from './constants';
import {getSingleColumnWidth, isMainColumn} from './utils';
import {createStyleVerticalLineOnResize} from './utils/createStyleVerticalLineOnResize';

import type {Node, ElementConfig} from 'react';

import type {DataTableResizeState, Column, TreeDataObj} from './typedef';

type NativeProps = $Diff<ElementConfig<'div'>, {
    onResize: any,
    children: any,
}>;

export const TextAlign = {
    LEFT: 'LEFT',
    CENTER: 'CENTER',
    RIGHT: 'RIGHT'
};

const TEXT_ALIGN_CLASSES = {
    [TextAlign.LEFT]: null,
    [TextAlign.CENTER]: 'text-align text-align_center',
    [TextAlign.RIGHT]: 'text-align text-align_right'
};

export type CellProps<DataColumn = {||}> = NativeProps & {
    /**
     * @deprecated It will be removed in 6 months 09-15-2025. use className instead
     */
    mix?: string,
    column?: Column<DataColumn>,
    children?: (() => Node) | Node,
    index?: number,
    /**
     * @deprecated It will be removed in 6 months 09-15-2025. use onMouseEnter instead
     */
    setHover?: ?() => void,
    /**
     * @deprecated It will be removed in 6 months 09-15-2025. use onMouseLeave instead
     */
    removeHover?: ?() => void,
    onStartResize?: (column: Column<DataColumn>, key: number) => void,
    onResize?: (state: DataTableResizeState, column: Column<DataColumn>, key: number) => void,
    onStopResize?: (state: DataTableResizeState, column: Column<DataColumn>, key: number) => void,
    resizable?: boolean,
    headerCell?: boolean,
    treeDataObj?: ?TreeDataObj<{||}>,
    onExpandTreeNodes?: (TreeDataObj<{||}>) => void,
    align?: $Keys<typeof TextAlign>,
    onMount?: ?(cell: HTMLElement) => void,
    onUpdate?: (cell: HTMLElement) => void,
    verticalAlign?: $Keys<typeof VerticalAlign>,
    sortableCell?: boolean,
    hoverable?: boolean,
    isLastChild?: boolean,
    allowedDragArea?: boolean,
    tableId?: string,
};

export const TREE_INDENT = 16;
export const TREE_CHILD_INDENT = 22;

export const VerticalAlign = {
    TOP: 'TOP',
    MIDDLE: 'MIDDLE',
    BASELINE: 'BASELINE',
    BOTTOM: 'BOTTOM'
};

const VERTICAL_ALIGN_CLASSES = {
    [VerticalAlign.TOP]: null,
    [VerticalAlign.MIDDLE]: `${tableCellName}_v-align-middle`,
    [VerticalAlign.BASELINE]: `${tableCellName}_v-align-baseline`,
    [VerticalAlign.BOTTOM]: `${tableCellName}_v-align-bottom`
};

export class Cell<DataColumn> extends Component<CellProps<DataColumn>> {
    cell: ?HTMLDivElement;
    style: ?HTMLStyleElement;
    cellWidth: number = 0;

    static defaultProps = {
        align: TextAlign.LEFT,
        mix: '',
        resizable: false,
        headerCell: false,
        treeDataObj: null,
        verticalAlign: VerticalAlign.TOP,
        sortableCell: false,
        onResize: () => undefined,
        onStopResize: () => undefined,
        onStartResize: () => undefined,
        setHover: () => undefined,
        removeHover: () => undefined,
        hoverable: true
    };

    componentDidMount() {
        return this.props.onMount && this.cell ? this.props.onMount(this.cell) : null;
    }

    componentDidUpdate() {
        return this.props.onUpdate && this.cell ? this.props.onUpdate(this.cell) : null;
    }

    componentWillUnmount() {
        const {removeHover} = this.props;

        if (removeHover) {
            removeHover();
        }
        this.removeHighlightStyle();
    }

    getColumnWidth = <T>(column?: Column<T>) => {
        return column ? getSingleColumnWidth(column) : 0;
    };

    onStartResize = () => {
        const {index, tableId, column, isLastChild, resizable} = this.props;
        if (column && index !== undefined) {
            const isResizable = column.resizable || resizable;
            if (isResizable) {
                this.style = createStyleVerticalLineOnResize({
                    isFixed: column.fixed,
                    isLastChild,
                    columnIndex: index,
                    tableId: tableId || '',
                });

                if (document.head) {
                    document.head.appendChild(this.style);
                }
            }

            this.props.onStartResize(column, index);
        }
    };

    onStopResize = (state: DataTableResizeState) => {
        const {column, index} = this.props;

        if (column && index !== undefined) {
            if (state.width !== getSingleColumnWidth(column)) {
                this.props.onStopResize(state, column, index);
            }
            this.removeHighlightStyle();
        }
    };

    removeHighlightStyle = () => {
        if (document.head && this.style) {
            document.head.removeChild(this.style);
            this.style = null;
        }
    };

    cellContent = (children: ?Node) => {
        const {onExpandTreeNodes, treeDataObj} = this.props;
        if (!treeDataObj) {
            return null;
        }

        const onTreeChange = ({event}) => {
            const {checked} = event.currentTarget;
            if (onExpandTreeNodes && treeDataObj) {
                onExpandTreeNodes({
                    ...treeDataObj,
                    expanded: checked
                });
            }
        };

        const {level, expanded, isTotal, hideCheckbox, isFlat} = treeDataObj;
        const isTotalChild = isTotal && expanded === undefined;

        const getCheckBoxArea = (condition: boolean) => (condition ? (
            <CheckBox
                name="tree-expander"
                mix="data-table__tree-expander"
                label={children}
                custom={true}
                checked={expanded}
                onCheckBoxChange={onTreeChange}
            />
        ) : <div style={{marginLeft: TREE_CHILD_INDENT}}>{children}</div>);

        return (
            <div
                className="data-table__checkbox-indent"
                style={{marginLeft: isTotalChild ? (level - 1) * TREE_INDENT : level * TREE_INDENT}}>
                {isFlat ? children : getCheckBoxArea(!hideCheckbox)}
            </div>
        );
    };

    renderCell = (cellWidth: number = this.cellWidth, resize: boolean = false, nodeRef = null) => {
        const {
            align,
            verticalAlign,
            hoverable,
            index,
            column,
            mix,
            setHover,
            removeHover,
            headerCell,
            treeDataObj,
            sortableCell,
            allowedDragArea,
            children,
            /* eslint-disable no-unused-vars */
            onStartResize,
            onResize,
            onStopResize,
            resizable,
            onExpandTreeNodes,
            onMount,
            onUpdate,
            isLastChild,
            tableId,
            /* eslint-enable no-unused-vars */
            style,
            className,
            onMouseEnter,
            onMouseLeave,
            ...nativeProps
        } = this.props;

        const cellChildren = typeof children === 'function' ? children() : children;
        const itemId = (sortableCell || allowedDragArea) ? {'data-id': column && column.columnId || ''} : {};
        const classnameIndex = index !== undefined ? tableCellName + '-' + index : '';

        return (
            <div
                {...nativeProps}
                {...itemId}
                ref={el => {
                    this.cell = el;

                    if (nodeRef) {
                        nodeRef.current = el;
                    }
                }}
                style={{
                    ...style,
                    ...!resize || cellWidth > 0 ? {width: cellWidth} : {},
                    ...sortableCell && headerCell ? { cursor: 'move' } : {},
                }}
                className={classNames(
                    tableCellName,
                    TEXT_ALIGN_CLASSES[align],
                    VERTICAL_ALIGN_CLASSES[verticalAlign],
                    mix,
                    className,
                    {
                        [headerCellName]: headerCell,
                        'data-table__cell_hoverable': headerCell && hoverable,
                        'native-sortable__item': sortableCell,
                        [(classnameIndex: string)]: classnameIndex
                    },
                )}
                onMouseEnter={onMouseEnter || setHover}
                onMouseLeave={onMouseLeave || removeHover}>
                {treeDataObj && column && isMainColumn(column) ? this.cellContent(cellChildren) : cellChildren}

                {this.isResizable(cellWidth) ? (
                    <div className={classNames(
                        handleClass,
                        SORTABLE_FILTER, {
                            [handleClassResize]: resize
                        })}
                    />
                ) : null}
            </div>
        );
    };

    isResizable = (width: number) => width && this.props.headerCell && this.props.resizable;

    render() {
        this.cellWidth = this.getColumnWidth(this.props.column);
        const {column, onResize, index} = this.props;
        const minWidth = column && column.minWidth ? {minWidth: Number(column.minWidth)} : {};
        const maxWidth = column && column.maxWidth ? {maxWidth: Number(column.maxWidth)} : {};

        return this.isResizable(this.cellWidth) ? (
            <DataTableResize
                key={this.cellWidth}
                {...{
                    width: this.cellWidth,
                    onResize,
                    onStopResize: this.onStopResize,
                    onStartResize: this.onStartResize,
                    ...minWidth,
                    ...maxWidth
                }}
                handle={handleClass}>

                {(state: DataTableResizeState, nodeRef) => {
                    if (state.resize && column && index !== undefined) {
                        onResize(state, column, index);
                    }

                    return this.renderCell(state.width, state.resize || false, nodeRef);
                }}

            </DataTableResize>
        ) : this.renderCell();
    }
}

            // @pb/generate-flow-typed Webpack Loader:
            // Duplicates all internal dependencies imports
            // to be sure all deps are parsed by this loader
            // cause Babel loader removes `import type ...`
            // and e.g. 'typedef.js' files are not included in dist
            
require("./typedef");
